import { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import { useAuthentication, useNotifications } from '../../store/AppContext';
import { DESIGN_TOKENS } from '../../styles/theme';
import { EmptyState, ErrorDisplay, LoadingSpinner, PilotCard } from '../../components/common';
import { useFetch, useUserProfile } from '../../hooks';
import {
  acceptApplication,
  fetchListingById,
  fetchReceivedApplications,
  fetchSentApplications,
  rejectApplication,
  sendApplicationMessage,
  startListing,
} from '../../api/services';
import type {
  ApplicationMessageSenderType,
  Listing,
  SubmittedApplication,
  SubmittedApplicationStatus,
} from '../../types';
import { pl } from '../../i18n';

export interface PilotApplicationsListSectionProps {
  emptyTitle: string;
  emptyDescription: string;
  loadingMessage: string;
  showAssignedClosedOnly: boolean;
}

type ReceivedApplicationGroup = {
  key: string;
  listing?: Listing;
  listingDocumentId?: string;
  title: string;
  applications: SubmittedApplication[];
  latestApplication?: SubmittedApplication;
};

export interface CompanyApplicationsSectionProps {
  listingStatus: 'OPEN' | 'CLOSED';
  emptyTitle: string;
  emptyDescription: string;
  detailBasePath: string;
  backPath: string;
  showStartAction: boolean;
}

type CompanyPilotFilter = 'ALL' | 'ACCEPTED' | 'REJECTED';

export type ApplicationConversationEntry = {
  key: string;
  content: string;
  senderType: ApplicationMessageSenderType;
  senderLabel: string;
  createdAt?: string;
  isInitial?: boolean;
};

const APPLICATION_STATUS_CONFIG = {
  PENDING: { label: 'Oczekuje', color: 'default' },
  ACCEPTED: { label: 'Przypisany', color: 'success' },
  REJECTED: { label: 'Odrzucony', color: 'error' },
} as const;

function getApplicationListing(app: SubmittedApplication) {
  return app.listing && typeof app.listing === 'object' ? app.listing : undefined;
}

function isClosedApplication(app: SubmittedApplication) {
  return getApplicationListing(app)?.listing_status === 'CLOSED';
}

function isClosedAssignedApplication(app: SubmittedApplication) {
  return app.status === 'ACCEPTED' && isClosedApplication(app);
}

function getApplicationStatusConfig(status?: SubmittedApplicationStatus | string | null) {
  if (status === 'ACCEPTED' || status === 'REJECTED' || status === 'PENDING') {
    return APPLICATION_STATUS_CONFIG[status];
  }

  return APPLICATION_STATUS_CONFIG.PENDING;
}

function getPilotApplicationStatusMessage(status?: SubmittedApplicationStatus | string | null) {
  if (status === 'ACCEPTED') {
    return {
      severity: 'success' as const,
      message: 'Jestes przypisany do tego zlecenia.',
    };
  }

  if (status === 'REJECTED') {
    return {
      severity: 'warning' as const,
      message: 'To zgloszenie zostalo odrzucone albo odwolane przez firme transportowa.',
    };
  }

  return {
    severity: 'info' as const,
    message: 'To zgloszenie oczekuje na decyzje firmy transportowej.',
  };
}

export function getApplicationConversation(application: SubmittedApplication): ApplicationConversationEntry[] {
  const entries: ApplicationConversationEntry[] = [];

  if (application.content?.trim()) {
    entries.push({
      key: `initial-${application.documentId ?? application.id ?? 'application'}`,
      content: application.content.trim(),
      senderType: 'PILOT',
      senderLabel: 'Pilot',
      createdAt: application.createdAt,
      isInitial: true,
    });
  }

  (application.messages ?? []).forEach((message) => {
    if (!message?.content?.trim()) return;

    entries.push({
      key: message.documentId ?? `message-${message.id ?? entries.length}`,
      content: message.content.trim(),
      senderType: message.sender_type,
      senderLabel: message.sender_type === 'COMPANY' ? 'Firma transportowa' : 'Pilot',
      createdAt: message.createdAt,
    });
  });

  return entries.sort((first, second) => {
    const firstDate = first.createdAt ? new Date(first.createdAt).getTime() : 0;
    const secondDate = second.createdAt ? new Date(second.createdAt).getTime() : 0;
    return firstDate - secondDate;
  });
}

interface ApplicationConversationProps {
  application: SubmittedApplication;
  currentSenderType: ApplicationMessageSenderType;
  draft: string;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  isSending: boolean;
}

export function ApplicationConversation({
  application,
  currentSenderType,
  draft,
  onDraftChange,
  onSend,
  isSending,
}: ApplicationConversationProps) {
  const conversation = getApplicationConversation(application);

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        KONWERSACJA
      </Typography>

      {conversation.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {conversation.map((message) => {
            const isCurrentUser = message.senderType === currentSenderType;

            return (
              <Box
                key={message.key}
                sx={{
                  alignSelf: isCurrentUser ? 'flex-end' : 'flex-start',
                  maxWidth: { xs: '100%', sm: '85%' },
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  backgroundColor: isCurrentUser
                    ? DESIGN_TOKENS.colors.primaryLighter
                    : DESIGN_TOKENS.colors.background,
                  border: `1px solid ${
                    isCurrentUser
                      ? `${DESIGN_TOKENS.colors.primary}20`
                      : DESIGN_TOKENS.colors.border
                  }`,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  {message.senderLabel}
                  {message.isInitial ? ' • wiadomość startowa' : ''}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {message.content}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {message.createdAt
                    ? new Date(message.createdAt).toLocaleString('pl-PL')
                    : 'Nieznana data'}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Alert severity="info">Brak wiadomości w tej aplikacji.</Alert>
      )}

      <Box sx={{ mt: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <TextField
          label="Wyślij wiadomość"
          multiline
          minRows={3}
          value={draft}
          onChange={(event) => onDraftChange(event.target.value)}
          placeholder="Napisz odpowiedź..."
          fullWidth
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={onSend}
            disabled={isSending || !draft.trim() || !application.documentId}
          >
            {isSending ? 'Wysyłanie...' : 'Wyślij wiadomość'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export function PilotApplicationsListSection({
  emptyTitle,
  emptyDescription,
  loadingMessage,
  showAssignedClosedOnly,
}: PilotApplicationsListSectionProps) {
  const navigate = useNavigate();
  const { currentUser } = useAuthentication();
  const { showNotification } = useNotifications();
  const { data: profile } = useUserProfile();
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [sendingMessageId, setSendingMessageId] = useState<string | null>(null);

  const { data: applicationsData, isLoading, error, refetch } = useFetch(
    () => fetchSentApplications(currentUser?.membership?.pilot?.documentId ?? ''),
    [profile?.id],
    { enabled: !!profile && profile.type === 'PILOT' }
  );

  const applications: SubmittedApplication[] = applicationsData?.data ?? [];
  const filteredApplications = applications.filter((app) =>
    showAssignedClosedOnly ? isClosedAssignedApplication(app) : !isClosedApplication(app)
  );

  const handleDraftChange = (applicationDocumentId: string, value: string) => {
    setReplyDrafts((previous) => ({
      ...previous,
      [applicationDocumentId]: value,
    }));
  };

  const handleSendMessage = async (application: SubmittedApplication) => {
    if (!application.documentId) return;

    const applicationDocumentId = application.documentId;
    const draft = replyDrafts[applicationDocumentId]?.trim();
    if (!draft) return;

    setSendingMessageId(applicationDocumentId);
    try {
      await sendApplicationMessage(applicationDocumentId, draft);
      setReplyDrafts((previous) => ({
        ...previous,
        [applicationDocumentId]: '',
      }));
      showNotification('Wiadomość została wysłana.', 'success');
      refetch();
    } catch (err: any) {
      showNotification(err.message || 'Nie udało się wysłać wiadomości.', 'error');
    } finally {
      setSendingMessageId(null);
    }
  };

  if (isLoading) {
    return <LoadingSpinner message={loadingMessage} />;
  }

  if (error) {
    return (
      <ErrorDisplay
        message={error}
        onRetry={() => {
          if (error) refetch();
        }}
      />
    );
  }

  if (filteredApplications.length === 0) {
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        actionLabel="Przeglądaj ogłoszenia"
        onAction={() => navigate('/listings')}
      />
    );
  }

  return (
    <Grid container spacing={3}>
      {filteredApplications.map((app) => (
        <Grid item xs={12} key={app.documentId ?? app.id}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    DOTYCZY OGŁOSZENIA
                  </Typography>
                  {app.listing && typeof app.listing === 'object' ? (
                    <>
                      <Typography variant="h6" fontWeight={700} gutterBottom>
                        {app.listing.basic_information?.title ?? 'Ogloszenie bez tytulu'}
                      </Typography>
                      <Button
                        component={RouterLink}
                        to={`/listings/${app.listing.documentId}`}
                        size="small"
                        variant="outlined"
                        sx={{ mt: 1 }}
                      >
                        Zobacz ogłoszenie
                      </Button>
                    </>
                  ) : (
                    <Typography variant="body2">Nieznane ogłoszenie</Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={8}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip
                      label={getApplicationStatusConfig(app.status).label}
                      color={getApplicationStatusConfig(app.status).color}
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                    {isClosedAssignedApplication(app) && (
                      <Chip
                        label="Zamknięte ogłoszenie"
                        color="default"
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 700 }}
                      />
                    )}
                    <Typography variant="body2" fontWeight={600}>
                      Status aplikacji
                    </Typography>
                  </Box>
                  <Alert severity={getPilotApplicationStatusMessage(app.status).severity} sx={{ mb: 2 }}>
                    {getPilotApplicationStatusMessage(app.status).message}
                  </Alert>
                  <ApplicationConversation
                    application={app}
                    currentSenderType="PILOT"
                    draft={app.documentId ? (replyDrafts[app.documentId] ?? '') : ''}
                    onDraftChange={(value) => {
                      if (!app.documentId) return;
                      handleDraftChange(app.documentId, value);
                    }}
                    onSend={() => handleSendMessage(app)}
                    isSending={sendingMessageId === app.documentId}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

export function CompanyApplicationsSection({
  listingStatus,
  emptyTitle,
  emptyDescription,
  detailBasePath,
  backPath,
  showStartAction,
}: CompanyApplicationsSectionProps) {
  const { listingId } = useParams<{ listingId?: string }>();
  const { showNotification } = useNotifications();
  const [applicationActionId, setApplicationActionId] = useState<string | null>(null);
  const [isStartingListing, setIsStartingListing] = useState(false);
  const [pilotFilter, setPilotFilter] = useState<CompanyPilotFilter>('ALL');
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [sendingMessageId, setSendingMessageId] = useState<string | null>(null);
  const {
    data: profile,
    isLoading: isProfileLoading,
    error: profileError,
    refetch: refetchProfile,
  } = useUserProfile();

  const { data: applicationsData, isLoading, error, refetch } = useFetch(
    () => fetchReceivedApplications(profile!.documentId, listingId, listingStatus),
    [profile?.documentId, listingId, listingStatus],
    { enabled: !!profile && profile.type === 'COMPANY' }
  );

  const { data: selectedListingResponse, refetch: refetchSelectedListing } = useFetch(
    () => fetchListingById(listingId ?? ''),
    [listingId],
    { enabled: !!listingId }
  );

  const applications = applicationsData?.data ?? [];
  const filteredApplications = applications.filter((application) => {
    if (pilotFilter === 'ALL') return true;
    return application.status === pilotFilter;
  });

  const applicationGroups = useMemo<ReceivedApplicationGroup[]>(() => {
    const groups = new Map<string, ReceivedApplicationGroup>();

    filteredApplications.forEach((application) => {
      const listing = application.listing && typeof application.listing === 'object'
        ? application.listing
        : undefined;
      const listingDocumentId = listing?.documentId ?? (
        typeof application.listing === 'string' ? application.listing : undefined
      );
      const key = listingDocumentId ?? `unknown-${application.documentId ?? application.id ?? groups.size}`;
      const existingGroup = groups.get(key);

      if (existingGroup) {
        existingGroup.applications.push(application);
        const existingDate = existingGroup.latestApplication?.createdAt
          ? new Date(existingGroup.latestApplication.createdAt).getTime()
          : 0;
        const currentDate = application.createdAt
          ? new Date(application.createdAt).getTime()
          : 0;

        if (currentDate >= existingDate) {
          existingGroup.latestApplication = application;
        }
        return;
      }

      groups.set(key, {
        key,
        listing,
        listingDocumentId,
        title: listing?.basic_information?.title ?? 'Ogłoszenie bez tytułu',
        applications: [application],
        latestApplication: application,
      });
    });

    return Array.from(groups.values()).sort((first, second) => {
      const firstDate = first.latestApplication?.createdAt
        ? new Date(first.latestApplication.createdAt).getTime()
        : 0;
      const secondDate = second.latestApplication?.createdAt
        ? new Date(second.latestApplication.createdAt).getTime()
        : 0;

      return secondDate - firstDate;
    });
  }, [filteredApplications]);

  const selectedListing = applications.find(
    (application) => application.listing && typeof application.listing === 'object'
  )?.listing;
  const selectedListingTitle =
    selectedListingResponse?.data?.basic_information?.title ??
    (selectedListing && typeof selectedListing === 'object'
      ? selectedListing.basic_information?.title
      : undefined);
  const selectedListingLimit =
    selectedListingResponse?.data?.pilots_quantity ??
    (selectedListing && typeof selectedListing === 'object'
      ? selectedListing.pilots_quantity
      : undefined);
  const selectedListingStatus =
    selectedListingResponse?.data?.listing_status ??
    (selectedListing && typeof selectedListing === 'object'
      ? selectedListing.listing_status
      : undefined);
  const acceptedApplicationsCount = applications.filter((app) => app.status === 'ACCEPTED').length;
  const canStartListing = Boolean(
    showStartAction &&
    listingId &&
    selectedListingLimit &&
    acceptedApplicationsCount >= selectedListingLimit &&
    selectedListingStatus !== 'CANCELED' &&
    selectedListingStatus !== 'CLOSED'
  );

  const handleDraftChange = (applicationDocumentId: string, value: string) => {
    setReplyDrafts((previous) => ({
      ...previous,
      [applicationDocumentId]: value,
    }));
  };

  const handleSendMessage = async (application: SubmittedApplication) => {
    if (!application.documentId) return;

    const applicationDocumentId = application.documentId;
    const draft = replyDrafts[applicationDocumentId]?.trim();
    if (!draft) return;

    setSendingMessageId(applicationDocumentId);
    try {
      await sendApplicationMessage(applicationDocumentId, draft);
      setReplyDrafts((previous) => ({
        ...previous,
        [applicationDocumentId]: '',
      }));
      showNotification('Wiadomość została wysłana do pilota.', 'success');
      refetch();
    } catch (err: any) {
      showNotification(err.message || 'Nie udało się wysłać wiadomości.', 'error');
    } finally {
      setSendingMessageId(null);
    }
  };

  const handleAcceptApplication = async (applicationDocumentId?: string) => {
    if (!listingId || !applicationDocumentId) return;
    if (!window.confirm('Czy na pewno chcesz przypisać tego pilota do zlecenia?')) {
      return;
    }

    setApplicationActionId(applicationDocumentId);
    try {
      await acceptApplication(listingId, applicationDocumentId);
      showNotification('Pilot został przypisany do zlecenia.', 'success');
      refetch();
      refetchSelectedListing();
    } catch (err: any) {
      showNotification(err.message || 'Nie udało się przypisać pilota.', 'error');
    } finally {
      setApplicationActionId(null);
    }
  };

  const handleRejectApplication = async (applicationDocumentId?: string) => {
    if (!listingId || !applicationDocumentId) return;

    setApplicationActionId(applicationDocumentId);
    try {
      await rejectApplication(listingId, applicationDocumentId);
      showNotification('Pilot został odwołany lub oferta została odrzucona.', 'success');
      refetch();
      refetchSelectedListing();
    } catch (err: any) {
      showNotification(err.message || 'Nie udało się odwołać pilota lub odrzucić oferty.', 'error');
    } finally {
      setApplicationActionId(null);
    }
  };

  const handleStartListing = async () => {
    if (!listingId) return;
    if (!window.confirm('Czy na pewno chcesz zamienić to ogłoszenie w zlecenie?')) {
      return;
    }

    setIsStartingListing(true);
    try {
      await startListing(listingId);
      showNotification('Ogłoszenie zostało zamienione w zlecenie.', 'success');
      refetch();
      refetchSelectedListing();
    } catch (err: any) {
      showNotification(err.message || 'Nie udało się zamienić ogłoszenia w zlecenie.', 'error');
    } finally {
      setIsStartingListing(false);
    }
  };

  if (isProfileLoading || isLoading) {
    return <LoadingSpinner message={pl.account.loadingReceivedApplications} />;
  }

  if (profileError || error) {
    return (
      <ErrorDisplay
        message={profileError ?? error ?? 'Wystąpił błąd'}
        onRetry={() => {
          if (profileError) refetchProfile();
          if (error) refetch();
        }}
      />
    );
  }

  if (!profile || profile.type !== 'COMPANY') {
    return (
      <EmptyState
        title={pl.common.noAccess}
        description={pl.account.receivedCompanyOnly}
      />
    );
  }

  return (
    <>
      {listingId && (
        <Card sx={{ mb: 3 }}>
          <CardContent
            sx={{
              p: 2.5,
              display: 'flex',
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                {listingStatus === 'OPEN' ? 'Oferty dla ogłoszenia' : 'Oferty dla zlecenia'}
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {selectedListingTitle ?? 'Wybrane ogłoszenie'}
              </Typography>
              {selectedListingLimit && (
                <Typography variant="body2" color="text.secondary">
                  Przypisani piloci: {acceptedApplicationsCount} / {selectedListingLimit}
                </Typography>
              )}
              {showStartAction && listingId && selectedListingLimit && acceptedApplicationsCount < selectedListingLimit && (
                <Typography variant="body2" color="text.secondary">
                  Zamiana w zlecenie będzie dostępna po skompletowaniu wszystkich pilotów.
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {showStartAction && (
                <Button
                  variant="contained"
                  disabled={!canStartListing || isStartingListing}
                  onClick={handleStartListing}
                >
                  Zamień w zlecenie
                </Button>
              )}
              {listingId && (
                <Button
                  component={RouterLink}
                  to={`/listings/${listingId}`}
                  variant="outlined"
                >
                  Przejdź do ogłoszenia
                </Button>
              )}
              {!showStartAction && listingId && (
                <Button
                  component={RouterLink}
                  to="#"
                  variant="outlined"
                >
                  Podgląd w czasie rzeczywistym konwoju
                </Button>
              )}
              <Button
                component={RouterLink}
                to={backPath}
                startIcon={<ArrowBackIcon />}
                variant="outlined"
              >
                {listingStatus === 'OPEN' ? 'Wszystkie oferty' : 'Wszystkie zlecenia'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent
          sx={{
            p: 2.5,
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Filtry pilotów
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Zawęź widok do przypisanych albo odwołanych pilotów.
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Status pilota</InputLabel>
            <Select
              value={pilotFilter}
              label="Status pilota"
              onChange={(event) => setPilotFilter(event.target.value as CompanyPilotFilter)}
            >
              <MenuItem value="ALL">Wszyscy</MenuItem>
              <MenuItem value="ACCEPTED">Tylko przypisani</MenuItem>
              <MenuItem value="REJECTED">Tylko odwołani</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {!listingId ? (
        applicationGroups.length > 0 ? (
          <Grid container spacing={3}>
            {applicationGroups.map((group) => (
              <Grid item xs={12} md={6} key={group.key}>
                <Card sx={{ height: '100%' }}>
                  <CardContent
                    sx={{
                      p: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                          {listingStatus === 'OPEN' ? 'Ogłoszenie' : 'Zlecenie'}
                        </Typography>
                        <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
                          {group.title}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          minWidth: 72,
                          px: 1.5,
                          py: 1,
                          borderRadius: 1,
                          textAlign: 'center',
                          backgroundColor: DESIGN_TOKENS.colors.primaryLighter,
                          border: `1px solid ${DESIGN_TOKENS.colors.primary}20`,
                        }}
                      >
                        <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1 }}>
                          {group.applications.length}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ofert
                        </Typography>
                      </Box>
                    </Box>

                    <Divider />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Ostatnia odpowiedź:{' '}
                        {group.latestApplication?.createdAt
                          ? new Date(group.latestApplication.createdAt).toLocaleString('pl-PL')
                          : 'Nieznana data'}
                      </Typography>
                      {group.latestApplication?.pilot_b2b &&
                      typeof group.latestApplication.pilot_b2b === 'object' &&
                      group.latestApplication.pilot_b2b.personal_data ? (
                        <Typography variant="body2" color="text.secondary">
                          Ostatni pilot: {group.latestApplication.pilot_b2b.personal_data.name}{' '}
                          {group.latestApplication.pilot_b2b.personal_data.surname}
                        </Typography>
                      ) : null}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      {group.listingDocumentId && (
                        <Button
                          component={RouterLink}
                          to={`${detailBasePath}/${group.listingDocumentId}`}
                          variant="contained"
                          startIcon={<RequestPageIcon />}
                        >
                          {listingStatus === 'OPEN' ? 'Zobacz odpowiedzi' : 'Zobacz zlecenie'}
                        </Button>
                      )}
                      {group.listingDocumentId && (
                        <Button
                          component={RouterLink}
                          to={`/listings/${group.listingDocumentId}`}
                          variant="outlined"
                        >
                          Szczegóły
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <EmptyState
            title={emptyTitle}
            description={emptyDescription}
          />
        )
      ) : filteredApplications.length > 0 ? (
        <Grid container spacing={4}>
          {filteredApplications.map((app) => (
            <Grid item xs={12} key={app.documentId ?? app.id}>
              <Card sx={{ overflow: 'visible' }}>
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        PILOT
                      </Typography>
                      {app.pilot_b2b &&
                      typeof app.pilot_b2b === 'object' &&
                      app.pilot_b2b.personal_data &&
                      Array.isArray(app.pilot_b2b.permissions) ? (
                        <PilotCard pilot={app.pilot_b2b} />
                      ) : (
                        <Typography variant="body2">Nieznany pilot</Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} md={8}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        SZCZEGÓŁY APLIKACJI
                      </Typography>
                      <Box
                        sx={{
                          p: 3,
                          backgroundColor: DESIGN_TOKENS.colors.primaryLighter,
                          borderRadius: 2,
                          border: `1px solid ${DESIGN_TOKENS.colors.primary}20`,
                          height: '100%',
                        }}
                      >
                        {(app.net_price != null || app.tax != null) && (
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 2,
                              flexWrap: 'wrap',
                              mt: 3,
                              pt: 2,
                              borderTop: `1px solid ${DESIGN_TOKENS.colors.border}`,
                            }}
                          >
                            {app.net_price != null && (
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Cena netto
                                </Typography>
                                <Typography variant="body2" fontWeight={700}>
                                  {app.net_price.toLocaleString('pl-PL')} PLN
                                </Typography>
                              </Box>
                            )}
                            {app.tax != null && (
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  VAT
                                </Typography>
                                <Typography variant="body2" fontWeight={700}>
                                  {app.tax}%
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                        <ApplicationConversation
                          application={app}
                          currentSenderType="COMPANY"
                          draft={app.documentId ? (replyDrafts[app.documentId] ?? '') : ''}
                          onDraftChange={(value) => {
                            if (!app.documentId) return;
                            handleDraftChange(app.documentId, value);
                          }}
                          onSend={() => handleSendMessage(app)}
                          isSending={sendingMessageId === app.documentId}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: { xs: 'stretch', sm: 'center' },
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 1.5,
                            mt: 3,
                          }}
                        >
                          <Chip
                            label={getApplicationStatusConfig(app.status).label}
                            color={getApplicationStatusConfig(app.status).color}
                            size="small"
                            sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, fontWeight: 700 }}
                          />
                          {listingId && showStartAction && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {app.status !== 'ACCEPTED' && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  disabled={!app.documentId || applicationActionId === app.documentId}
                                  onClick={() => handleAcceptApplication(app.documentId)}
                                >
                                  {app.status === 'REJECTED' ? 'Przypisz ponownie' : 'Przypisz pilota'}
                                </Button>
                              )}
                              {app.status !== 'REJECTED' && (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  disabled={!app.documentId || applicationActionId === app.documentId}
                                  onClick={() => handleRejectApplication(app.documentId)}
                                >
                                  {app.status === 'ACCEPTED' ? 'Odwołaj pilota' : 'Odrzuć'}
                                </Button>
                              )}
                            </Box>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'right' }}>
                          Otrzymano: {app.createdAt ? new Date(app.createdAt).toLocaleString('pl-PL') : 'Nieznana data'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <EmptyState
          title={emptyTitle}
          description={
            pilotFilter === 'ALL'
              ? (listingId ? 'Brak ofert dla wybranego wpisu.' : emptyDescription)
              : 'Brak pilotów pasujących do wybranego filtra.'
          }
        />
      )}
    </>
  );
}
